import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/AxiosInstance";

const MODULE_OPTIONS = [
  "All",
  "User Management",
  "Access Management",
  "Reporting & Analytics",
];

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [reportNameMap, setReportNameMap] = useState({});

  const fetchLogs = async ({ page = currentPage, searchText = search } = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", "20");
      if (searchText.trim()) {
        params.set("search", searchText.trim());
      }
      if (moduleFilter !== "All") {
        params.set("module", moduleFilter);
      }

      const response = await axiosInstance.get(`/activity-logs?${params.toString()}`);
      if (response.status === 200) {
        setLogs(response.data.logs || []);
        console.log("Fetched logs:", response.data.logs[0].details || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalRecords(response.data.totalRecords || 0);
      }
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
      setLogs([]);
      setTotalPages(1);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [currentPage, moduleFilter]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      setCurrentPage(1);
      fetchLogs({ page: 1, searchText: search });
    }, 400);
    return () => clearTimeout(debounce);
  }, [search]);

  useEffect(() => {
    const fetchReportNames = async () => {
      try {
        const response = await axiosInstance.get("/bookmarks");
        if (response.status === 200) {
          const map = {};
          (response.data || []).forEach((bookmark) => {
            if (bookmark?._id) {
              map[String(bookmark._id)] = bookmark.title || "Unknown";
            }
            if (bookmark?.report?.id) {
              map[String(bookmark.report.id)] = bookmark.title || bookmark.report.name || "Unknown";
            }
          });
          setReportNameMap(map);
        }
      } catch (error) {
        console.error("Failed to fetch report names:", error);
      }
    };
    fetchReportNames();
  }, []);

  const formatActionLabel = (value = "") =>
    value
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getModuleBadgeClass = (moduleName) => {
    if (moduleName === "User Management") {
      return "bg-red-100 text-[#E31c23]";
    }
    if (moduleName === "Access Management") {
      return "bg-amber-100 text-amber-700";
    }
    return "bg-blue-100 text-blue-700";
  };

  const getIdLabel = (id) => {
    const idAsString = String(id || "");
    const name = reportNameMap[idAsString];
    return name ? `${name} (${idAsString})` : idAsString;
  };

  const formatDetailValue = (key, value) => {
    if (Array.isArray(value)) {
      const shouldResolveIds =
        key.toLowerCase().includes("reportid") ||
        key.toLowerCase().includes("applicationid") ||
        key.toLowerCase().includes("finalreportids") ||
        key.toLowerCase().includes("addedreportids") ||
        key.toLowerCase().includes("removedreportids");

      if (shouldResolveIds) {
        return value.map((id) => getIdLabel(id)).join(", ");
      }

      if (value.every((item) => item && typeof item === "object" && item.id)) {
        return value
          .map((item) => `${item.name || reportNameMap[String(item.id)] || "Unknown"} (${item.id})`)
          .join(", ");
      }
      return value.map((item) => (typeof item === "object" ? JSON.stringify(item) : item)).join(", ");
    }

    if (value && typeof value === "object") {
      if (value.id) {
        return `${value.name || reportNameMap[String(value.id)] || "Unknown"} (${value.id})`;
      }
      return JSON.stringify(value);
    }

    return value;
  };

  const getDetailsPreview = (details = {}) => {
    const entries = Object.entries(details || {});
    if (!entries.length) {
      return "-";
    }
    const reportLikeEntries = entries.filter(([key]) => {
      const normalized = key.toLowerCase();
      return (
        normalized.includes("report") ||
        normalized.includes("application")
      );
    });

    const nonReportEntries = entries.filter(([key]) => {
      const normalized = key.toLowerCase();
      return !(
        normalized.includes("report") ||
        normalized.includes("application")
      );
    });

    const previewEntries = [...nonReportEntries.slice(0, 2), ...reportLikeEntries].slice(
      0,
      4
    );

    return previewEntries
      .map(([key, value]) => `${key}: ${formatDetailValue(key, value)}`)
      .join(" | ");
  };

  const pageLabel = useMemo(
    () => `Page ${currentPage} of ${Math.max(totalPages, 1)}`,
    [currentPage, totalPages]
  );

  return (
    <div className="w-full h-full flex flex-col">
      <div className="mb-3 rounded-lg border border-red-100 bg-white px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-800 leading-tight">Activity Logs</h1>
            <p className="text-xs text-gray-500 truncate">
              {totalRecords} records | {moduleFilter} | {pageLabel}
            </p>
          </div>
          <button
            onClick={() => fetchLogs({ page: currentPage })}
            className="rounded-md border border-red-200 px-2.5 py-1 text-xs font-medium text-[#E31c23] hover:bg-red-50"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex gap-2 w-full md:ml-auto md:w-auto">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by actor, target, action..."
            className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full md:w-72 focus:border-[#E31c23] focus:outline-none"
          />
          <select
            value={moduleFilter}
            onChange={(e) => {
              setModuleFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:border-[#E31c23] focus:outline-none"
          >
            {MODULE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr>
                <th className="p-3 text-left">Timestamp</th>
                <th className="p-3 text-left">Module</th>
                <th className="p-3 text-left">Activity</th>
                <th className="p-3 text-left">Performed By</th>
                <th className="p-3 text-left">Affected User</th>
                <th className="p-3 text-left">Context</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-500">
                    Loading activity logs...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-gray-500">
                    No activity found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log._id} className="border-t border-gray-100 align-top hover:bg-red-50/30">
                    <td className="p-3 whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="p-3">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getModuleBadgeClass(log.module)}`}>
                        {log.module}
                      </span>
                    </td>
                    <td className="p-3 font-medium text-gray-800">
                      {formatActionLabel(log.action)}
                    </td>
                    <td className="p-3 break-all">{log.actorEmail}</td>
                    <td className="p-3 break-all">{log.targetEmail || "-"}</td>
                    <td className="p-3 text-xs text-gray-600 break-words max-w-[420px]">
                      {getDetailsPreview(log.details)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="md:hidden p-3 space-y-3">
          {loading ? (
            <div className="text-center text-sm text-gray-500 py-4">Loading activity logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-4">No activity found</div>
          ) : (
            logs.map((log) => (
              <div key={log._id} className="rounded-lg border border-gray-200 p-3 bg-white shadow-sm">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <span className={`rounded-full px-2 py-1 text-[11px] font-medium ${getModuleBadgeClass(log.module)}`}>
                    {log.module}
                  </span>
                  <span className="text-[11px] text-gray-500">
                    {new Date(log.createdAt).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  {formatActionLabel(log.action)}
                </p>
                <p className="text-xs text-gray-600 break-all">
                  <span className="font-medium text-gray-700">By:</span> {log.actorEmail}
                </p>
                <p className="text-xs text-gray-600 break-all">
                  <span className="font-medium text-gray-700">Target:</span> {log.targetEmail || "-"}
                </p>
                <p className="text-xs text-gray-600 mt-2 break-words">
                  <span className="font-medium text-gray-700">Context:</span> {getDetailsPreview(log.details)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 mt-3">
        <span className="text-xs text-gray-600">{pageLabel}</span>
        <div className="flex gap-2">
          <button
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            className="px-3 py-1.5 border rounded text-sm disabled:opacity-40"
          >
            Previous
          </button>
          <button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            className="px-3 py-1.5 bg-[#E31c23] text-white rounded text-sm disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs;
